/**
 * GA4 Section & Button Click Tracking
 *
 * Global, zero-config click tracking. On every page it listens for clicks on
 * meaningful interactive elements (links, buttons, CTAs) and publishes an
 * `oobli:section_click` custom event through Shopify's Customer Events API
 * (`Shopify.analytics.publish`). A custom web pixel (Shopify admin →
 * Settings → Customer events) subscribes to the event and forwards it to
 * GA4, so the client can see which buttons and sections visitors interact
 * with on each page. See docs/audits/2026-06-18-ga4-tracking-audit-OOB-467.md
 * for the pixel setup.
 *
 * Consent: this script only publishes when Shopify's Customer Privacy API reports
 * analytics consent (Shopify.customerPrivacy.analyticsProcessingAllowed()). If that
 * API isn't loaded it proceeds, and the forwarding pixel — which runs under the
 * same Analytics permission — remains the authoritative consent gate, so nothing
 * reaches GA4 without consent either way.
 *
 * Debugging: when GA4_DEBUG is on, the script logs a boot line (with the current
 * consent state), every fired event, consent-suppressed clicks, and consent
 * changes. GA4_DEBUG is automatic in dev/QA contexts — the local
 * `shopify theme dev` preview, the Shopify theme preview ("preview bar"), and the
 * theme editor — and can be forced anywhere with ?ga4debug or
 * localStorage.ga4_debug='1'. It stays off on the live storefront.
 *
 * Each Shopify section is the unit of reporting. The section label is composed
 * at click time as "Page title › Section type (Section N)" — e.g.
 * "Sports Nutrition › Rich text (Section 7)". The page title comes from the
 * page's <h1>; the section type from a data-ga4-section attribute that section
 * files stamp with their type (falling back to parsing the DOM id); and the
 * index from the section's position among the page's body sections (computed
 * from the DOM, so it also works for static and AJAX-rendered sections, where
 * Liquid's section.index is nil).
 *
 * Optional data-* attributes refine the data when you want nicer labels:
 *   data-ga4-section="Hero Banner"  on a section wrapper      -> friendly section type
 *   data-ga4-label="Shop Now"       on a clickable element    -> friendly click label
 *   data-ga4-track                  on any element            -> force-track a non-standard element
 *   data-ga4-ignore                 on an element/subtree     -> exclude from tracking
 *
 * Event payload (arrives in the pixel as event.customData):
 *   {
 *     section_name:  'Sports Nutrition › Rich text (Section 7)', // composed human label
 *     section_type:  'rich text',          // section type alone (stable grouping key)
 *     section_index: 7,                     // position among body sections (null for header/footer)
 *     section_id:    'shopify-section-...', // DOM id of the section wrapper
 *     page_title:    'Sports Nutrition',    // the page's <h1> (or cleaned document.title)
 *     click_text:    'Shop Now',           // label / aria-label / value / text / alt / href
 *     click_element: 'a',                  // tag of the clicked element ('a', 'button', 'input:submit')
 *     click_url:     '/collections/all',   // href, for links only
 *     page_path:     '/pages/sweet-protein'
 *   }
 */
(function () {
  'use strict';

  // Elements that count as a trackable "button" / interactive element.
  var INTERACTIVE_SELECTOR = [
    'a[href]',
    'button',
    '[role="button"]',
    'input[type="submit"]',
    'input[type="button"]',
    '.button',
    '[data-ga4-track]'
  ].join(',');

  // Cap on captured text length, so verbose labels don't bloat the payload.
  var MAX_TEXT_LENGTH = 100;

  // GA4 silently truncates event-parameter VALUES at 100 chars. The composed
  // section_name puts "(Section N)" last, so an over-long page title would get
  // the section label chopped off — exactly the part we want to keep. We budget
  // the title so the whole string fits, trimming the (most variable) title with
  // an ellipsis and always preserving the section suffix.
  var MAX_SECTION_NAME_LENGTH = 100;

  // Dev/QA console heartbeat so you can watch section_click events fire in the
  // browser. On automatically in non-production contexts; off on the live
  // storefront so the visitor console stays quiet. Force it anywhere with a
  // `?ga4debug` query param or localStorage.setItem('ga4_debug','1').
  var GA4_DEBUG = (function () {
    try {
      var loc = window.location;
      var search = loc.search || '';

      // Manual override — works anywhere, including production, for a live check.
      if (/[?&]ga4debug(?:[=&]|$)/.test(search)) return true;
      // Local `shopify theme dev` preview.
      if (loc.hostname === '127.0.0.1' || loc.hostname === 'localhost') return true;
      // Theme editor.
      if (window.Shopify && window.Shopify.designMode) return true;
      // Shopify theme preview (the "preview bar" context). The param rides the
      // preview entry URL; remember it for the tab session so it survives the
      // in-preview navigation that can drop the query string.
      if (/[?&]preview_theme_id=/.test(search)) {
        try { window.sessionStorage.setItem('ga4_debug', '1'); } catch (e) {}
        return true;
      }
      if (window.sessionStorage && window.sessionStorage.getItem('ga4_debug') === '1') return true;
      return window.localStorage.getItem('ga4_debug') === '1';
    } catch (e) {
      return false;
    }
  })();

  function cleanText(value) {
    return (value || '').replace(/\s+/g, ' ').trim().slice(0, MAX_TEXT_LENGTH);
  }

  // Internal theme-builder prefixes that are meaningless to a human reading GA4.
  // Stripped as whole words from the reported section type, whether the type
  // came from a data-ga4-section stamp or was derived from the DOM id — e.g.
  // "newbird hero" -> "hero", "oobli up image divider" -> "up image divider".
  function normalizeSectionType(type) {
    var stripped = (type || '')
      .replace(/\b(?:newbird|oobli)\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
    return stripped || type;
  }

  // Returns a navigational href for a link, or null for anchors that don't
  // actually navigate ("#", empty, "javascript:..."). Keeps non-navigational
  // <a> elements (carousel arrows, "back to top", JS toggles) out of GA4.
  function navigationalHref(el) {
    if (el.tagName !== 'A') return null;
    var raw = (el.getAttribute('href') || '').trim();
    if (!raw || raw.charAt(0) === '#' || /^javascript:/i.test(raw)) return null;
    return raw;
  }

  // Derive a human-ish section type from a section wrapper.
  //
  // PRIMARY source: an explicit data-ga4-section attribute. Most section files
  // stamp their type onto their root element (a literal like
  // data-ga4-section="image with text" — the Liquid `section` object has no
  // `type` property, so the type is hardcoded per section file), making this the
  // reliable path; it always wins.
  //
  // FALLBACK (unstamped sections): parse the type out of the DOM id, which looks
  // like "shopify-section-template--1234567890__text_hero_pEPmXN" (JSON
  // templates) or "shopify-section-header" (static sections). CAVEAT: the id
  // suffix is the section *key*, which is the type only when the key is
  // "<type>_<nanoid>". A key that is a raw UUID carries no type, so it falls back
  // to 'unknown' — stamp data-ga4-section on that section to label it.
  function deriveSectionType(wrapperEl) {
    if (!wrapperEl) return 'unknown';

    var explicit = wrapperEl.getAttribute('data-ga4-section');
    if (explicit) return normalizeSectionType(cleanText(explicit));

    var key = (wrapperEl.id || '')
      .replace(/^shopify-section-/, '')
      .replace(/^template--\d+__/, '');

    // A UUID key carries no type — don't emit the raw hex as a "type".
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(key)) {
      return 'unknown';
    }

    var name = key
      // Shopify appends a random 6-char nanoid to editor-added section keys
      // ("text_hero_pEPmXN", "button_3EPj43"). Strip that trailing token so it
      // doesn't leak into the label — but only when it looks like a nanoid
      // (contains an uppercase letter or digit), so real lowercase trailing
      // words like "_button" or "_hero" are left intact.
      .replace(/_([A-Za-z0-9]{6})$/, function (full, token) {
        return /[A-Z0-9]/.test(token) ? '' : full;
      })
      .replace(/[-_]+/g, ' ')
      .trim();

    return normalizeSectionType(name) || 'unknown';
  }

  // Best-effort label for the clicked element.
  function describeElement(el) {
    var label = el.getAttribute('data-ga4-label');
    if (label) return cleanText(label);

    var aria = el.getAttribute('aria-label');
    if (aria) return cleanText(aria);

    // Inputs (submit/button) carry their label in the value attribute, not
    // textContent — e.g. <input type="submit" value="Subscribe">.
    if (el.tagName === 'INPUT') {
      var value = el.getAttribute('value');
      if (value) return cleanText(value);
    }

    var text = cleanText(el.textContent);
    if (text) return text;

    var img = el.querySelector('img[alt]');
    if (img && img.getAttribute('alt')) return cleanText(img.getAttribute('alt'));

    if (el.tagName === 'A') {
      var hrefLabel = cleanText(navigationalHref(el));
      if (hrefLabel) return hrefLabel;
    }

    return '(no label)';
  }

  // Tag name of the clicked element, refined for inputs so submit/button are
  // distinguishable — a bare tag name reports every input as just "input".
  // e.g. "a", "button", "input:submit".
  function describeElementType(el) {
    var tag = el.tagName.toLowerCase();
    if (tag === 'input') return 'input:' + (el.getAttribute('type') || 'text').toLowerCase();
    return tag;
  }

  // The page's human title: prefer a body <h1>, else a cleaned document.title
  // (Shopify appends " – {shop name}", so keep only the part before the separator).
  // The separator must be space-padded (" – ", " — ", " | ", " - "), so hyphenated
  // words like "Sugar-Free" are never split.
  function pageTitle() {
    var h1 = document.querySelector('#MainContent h1') || document.querySelector('h1');
    var fromH1 = h1 && cleanText(h1.textContent);
    if (fromH1) return fromH1;
    var title = (document.title || '').split(/\s[–—|-]\s/)[0];
    return cleanText(title) || 'Untitled page';
  }

  // 1-based position of the section among the page's body sections, matching the
  // visual order a human sees. Mirrors Liquid's section.index but is computed
  // from the DOM, so it also works for static and AJAX-rendered sections (where
  // section.index is nil) and naturally excludes header/footer (which live
  // outside #MainContent and so report a null index).
  //
  // We match only the DIRECT children of #MainContent. This theme (Dawn-based)
  // renders every template section flat there — verified: no nested
  // `.shopify-section` exists, since app content lives as blocks *inside* a
  // section, not as its own section wrapper. If a future layout ever nested a
  // `.shopify-section` (e.g. Horizon-style section groups), a click inside the
  // inner one would resolve to that inner wrapper, miss the direct-child set,
  // and report a null index — acceptable, and a signal to revisit this if it
  // happens.
  function sectionIndex(sectionEl) {
    if (!sectionEl) return null;
    var bodySections = document.querySelectorAll('#MainContent > .shopify-section');
    var pos = Array.prototype.indexOf.call(bodySections, sectionEl);
    return pos === -1 ? null : pos + 1;
  }

  // "rich text" -> "Rich text"; leaves an already-friendly data-ga4-section value intact.
  function capitalizeFirst(value) {
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
  }

  // Compose the human-readable label: "Page title › Section type (Section N)".
  // The "Section" word makes the number self-explanatory in GA4 reports. When
  // the type is unrecoverable ('unknown') we drop the "› Unknown" noise and just
  // read "Page title (Section N)". The page title is trimmed (with an ellipsis)
  // and the type is capped so the whole string stays within GA4's 100-char value
  // limit and the "(Section N)" suffix is never the part that gets chopped.
  function composeSectionName(page, type, index) {
    var indexPart = index ? ' (Section ' + index + ')' : '';
    var typePart = (type && type !== 'unknown')
      ? ' › ' + capitalizeFirst(cleanText(type).slice(0, 40))
      : '';
    var suffix = typePart + indexPart;
    var titleBudget = MAX_SECTION_NAME_LENGTH - suffix.length;
    var title = page;
    if (title.length > titleBudget) {
      title = titleBudget > 3
        ? title.slice(0, titleBudget - 3).trim() + '...'
        : title.slice(0, Math.max(0, titleBudget));
    }
    return (title + suffix).slice(0, MAX_SECTION_NAME_LENGTH);
  }

  // Whether the visitor has granted ANALYTICS consent, via Shopify's Customer
  // Privacy API — the same permission the forwarding pixel runs under, so this
  // mirrors what will actually reach GA4. Per Shopify's guidance, if the API
  // isn't loaded we proceed (the pixel still enforces consent server-side).
  function analyticsConsentGranted() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || typeof cp.analyticsProcessingAllowed !== 'function') return true;
      return cp.analyticsProcessingAllowed();
    } catch (e) {
      return true;
    }
  }

  // Human-readable consent state for the debug boot log (three states, since the
  // Customer Privacy API may not have loaded yet at script-init time).
  function analyticsConsentLabel() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || typeof cp.analyticsProcessingAllowed !== 'function') {
        return 'unknown (Customer Privacy API not loaded yet)';
      }
      return cp.analyticsProcessingAllowed() ? 'granted' : 'not granted';
    } catch (e) {
      return 'unknown';
    }
  }

  // Capture phase so the event is still recorded if a downstream handler calls
  // stopPropagation(). Delivery to the pixel sandbox is asynchronous, so an
  // event fired immediately before a hard navigation can be lost — accepted
  // trade-off, see "Known limitations" in the GA4 tracking audit doc.
  document.addEventListener(
    'click',
    function (event) {
      if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') return;

      // event.target is normally the clicked element, but guard against the rare
      // case where it isn't an Element (e.g. the document) and has no closest().
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var el = target.closest(INTERACTIVE_SELECTOR);
      if (!el) return;
      if (el.closest('[data-ga4-ignore]')) return;

      // Only emit when analytics consent is granted. The forwarding pixel already
      // gates delivery on the same consent, but checking here avoids publishing
      // events that would be dropped — and keeps the debug log honest about what
      // actually reaches GA4.
      if (!analyticsConsentGranted()) {
        if (GA4_DEBUG) {
          console.log(
            '%c GA4 ✕ %c section_click suppressed — analytics consent not granted',
            'background:#9a9a9a;color:#fff;border-radius:3px;padding:1px 4px',
            'color:#9a9a9a'
          );
        }
        return;
      }

      // section_name comes from the nearest data-ga4-section or section wrapper;
      // section_id always comes from the enclosing Shopify section, so a
      // data-ga4-section placed on an inner element still reports a real
      // section id rather than null.
      var labeledEl = el.closest('[data-ga4-section], .shopify-section');
      var sectionEl = el.closest('.shopify-section');
      var idEl = sectionEl || labeledEl;
      var href = navigationalHref(el);

      var sectionType = deriveSectionType(labeledEl);
      var index = sectionIndex(sectionEl);
      var page = pageTitle();

      var payload = {
        section_name: composeSectionName(page, sectionType, index),
        section_type: cleanText(sectionType),
        section_index: index,
        section_id: idEl ? idEl.id || null : null,
        page_title: page,
        click_text: describeElement(el),
        click_element: describeElementType(el),
        click_url: href,
        page_path: window.location.pathname
      };

      window.Shopify.analytics.publish('oobli:section_click', payload);

      // Visible heartbeat for QA (dev/preview only — see GA4_DEBUG).
      if (GA4_DEBUG) {
        console.log(
          '%c GA4 ▸ section_click %c ' + payload.section_name,
          'background:#040454;color:#fff;border-radius:3px;padding:1px 4px',
          'color:#040454;font-weight:bold',
          payload
        );
      }
    },
    true
  );

  // Shim-style boot line + live consent updates, so during QA you can see at a
  // glance that the tracker loaded and what consent state it's seeing. Dev/preview
  // only (GA4_DEBUG); silent on the live storefront.
  if (GA4_DEBUG) {
    console.log(
      '%c GA4 ▸ section tracking ready %c analytics consent: ' + analyticsConsentLabel() +
        ' — click a section to log an event',
      'background:#040454;color:#fff;border-radius:3px;padding:1px 4px',
      'color:#040454'
    );
    // Fires when the visitor responds to the cookie-consent banner.
    document.addEventListener('visitorConsentCollected', function (e) {
      console.log(
        '%c GA4 ▸ consent updated %c',
        'background:#040454;color:#fff;border-radius:3px;padding:1px 4px',
        'color:#040454',
        (e && e.detail) || null
      );
    });
  }
})();
